import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import BarcodeScanner from '../components/BarcodeScanner';
import ItemsHeader from '../components/items/ItemsHeader';
import ItemsTable from '../components/items/ItemsTable';
import ItemsCards from '../components/items/ItemsCards';
import ItemModal from '../components/items/ItemModal';
import '../styles/Items.css';

const computeCaseWeight = (weightG, qty) => {
  const w = parseFloat(weightG);
  const q = parseFloat(qty);
  if (!w || !q) return '';
  return ((w * q) / 1000).toFixed(2);
};

const EMPTY_FORM = {
  part_number: '',
  name: '',
  barcode: '',
  country_of_origin: '',
  raw_baked: '',
  weight_per_piece: '',
  qty_per_case: '',
  case_weight: '',
  fda_code: '',
  tariff_number: '',
  usmca: '',
  butter_fat_pct: '',
  sugar_pct: '',
  cocoa_pct: '',
  category_id: ''
};

const Items = () => {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [importing, setImporting] = useState(false);
  const [duplicateToast, setDuplicateToast] = useState(false);
  const fileInputRef = useRef(null);
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('itemsViewMode');
    if (saved) return saved;
    return window.innerWidth <= 1024 ? 'cards' : 'table';
  });

  useEffect(() => {
    fetchItems();
    fetchCategories();
  }, []);

  useEffect(() => {
    let filtered = items;
    if (selectedCategory) {
      filtered = filtered.filter(
        (item) => String(item.category_id) === selectedCategory
      );
    }
    if (searchTerm) {
      const words = searchTerm.toLowerCase().trim().split(/\s+/);
      filtered = filtered.filter((item) => {
        const text = [item.name, item.part_number].join(' ').toLowerCase();
        const barcode = (item.barcode || '').toLowerCase();
        const qtyStr = item.qty_per_case ? String(item.qty_per_case) : '';
        if (words.length === 1) {
          return text.includes(words[0]) || barcode.includes(words[0]) || qtyStr === words[0];
        }
        return words.every(word => text.includes(word) || qtyStr === word);
      });
    }
    setFilteredItems(filtered);
  }, [searchTerm, selectedCategory, items]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await api.get('/items');
      setItems(response.data);
      setFilteredItems(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (err) {
      // Categories are optional, don't block page load
    }
  };

  const handleViewItem = (item) => {
    setSelectedItem(item);
    setModalMode('view');
    setShowModal(true);
  };

  const handleAddItem = () => {
    setFormData(EMPTY_FORM);
    setSelectedItem(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleEditItem = (item) => {
    setFormData({
      part_number: item.part_number || '',
      name: item.name || '',
      barcode: item.barcode || '',
      country_of_origin: item.country_of_origin || '',
      raw_baked: item.raw_baked || '',
      weight_per_piece: item.weight_per_piece || '',
      qty_per_case: item.qty_per_case || '',
      case_weight: item.case_weight || '',
      fda_code: item.fda_code || '',
      tariff_number: item.tariff_number || '',
      usmca: item.usmca || '',
      butter_fat_pct: item.butter_fat_pct || '',
      sugar_pct: item.sugar_pct || '',
      cocoa_pct: item.cocoa_pct || '',
      category_id: item.category_id || ''
    });
    setSelectedItem(item);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteItem = async () => {
    try {
      await api.delete(`/items/${selectedItem.id}`);
      setShowDeleteConfirm(false);
      setShowModal(false);
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete item');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'add' || modalMode === 'duplicate') {
        const response = await api.post('/items', formData);
        setSelectedItem(response.data);
        setModalMode('view');
        fetchItems();
      } else if (modalMode === 'edit') {
        await api.put(`/items/${selectedItem.id}`, formData);
        const response = await api.get(`/items/${selectedItem.id}`);
        setSelectedItem(response.data);
        setModalMode('view');
        fetchItems();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save item');
    }
  };

  const handleSaveAndDuplicate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/items', formData);
      fetchItems();
      setFormData((prev) => ({ ...prev, part_number: '', barcode: '' }));
      setDuplicateToast(true);
      setTimeout(() => setDuplicateToast(false), 2500);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save item');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'weight_per_piece' || name === 'qty_per_case') {
        next.case_weight = computeCaseWeight(next.weight_per_piece, next.qty_per_case);
      }
      return next;
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setShowDeleteConfirm(false);
  };

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('itemsViewMode', mode);
  };

  const handleDuplicateItem = (item) => {
    setFormData({
      part_number: item.part_number || '',
      name: item.name || '',
      barcode: '',
      country_of_origin: item.country_of_origin || '',
      raw_baked: item.raw_baked || '',
      weight_per_piece: item.weight_per_piece || '',
      qty_per_case: item.qty_per_case || '',
      case_weight: item.case_weight || '',
      fda_code: item.fda_code || '',
      tariff_number: item.tariff_number || '',
      usmca: item.usmca || '',
      butter_fat_pct: item.butter_fat_pct || '',
      sugar_pct: item.sugar_pct || '',
      cocoa_pct: item.cocoa_pct || '',
      category_id: item.category_id || ''
    });
    setSelectedItem(null);
    setModalMode('duplicate');
    setShowModal(true);
  };

  const handleBarcodeScan = (barcode) => {
    setSearchTerm(barcode);
    setShowScanner(false);
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/items/export', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `items_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export items');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    // Parse header row
    const headers = parseCSVLine(lines[0]);

    // Parse data rows
    const items = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length !== headers.length) continue;

      const item = {};
      headers.forEach((header, index) => {
        item[header.trim()] = values[index]?.trim() || '';
      });
      items.push(item);
    }
    return items;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setError('');

    try {
      const text = await file.text();
      const items = parseCSV(text);

      if (items.length === 0) {
        throw new Error('No valid items found in CSV file');
      }

      const response = await api.post('/items/import', { items });
      alert(response.data.message);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to import items');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="items-page">
        <div className="loading">Loading items...</div>
      </div>
    );
  }

  return (
    <div className="items-page">
      <ItemsHeader
        isAdmin={isAdmin()}
        filteredCount={filteredItems.length}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onScan={() => setShowScanner(true)}
        viewMode={viewMode}
        onToggleView={toggleViewMode}
        onAddItem={handleAddItem}
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        onExport={handleExport}
        onImport={handleImportClick}
        importing={importing}
      />

      {/* Hidden file input for import */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".csv"
        onChange={handleFileSelect}
      />

      {error && <div className="error-message">{error}</div>}

      {viewMode === 'table' ? (
        <ItemsTable
          items={filteredItems}
          isAdmin={isAdmin()}
          onEdit={handleEditItem}
          onView={handleViewItem}
          onDuplicate={handleDuplicateItem}
        />
      ) : (
        <ItemsCards
          items={filteredItems}
          isAdmin={isAdmin()}
          onEdit={handleEditItem}
          onView={handleViewItem}
          onDuplicate={handleDuplicateItem}
        />
      )}

      <ItemModal
        isOpen={showModal}
        mode={modalMode}
        selectedItem={selectedItem}
        formData={formData}
        categories={categories}
        isAdmin={isAdmin()}
        showDeleteConfirm={showDeleteConfirm}
        duplicateToast={duplicateToast}
        existingBarcodes={items.filter(i => i.barcode).map(i => i.barcode.trim())}
        onClose={closeModal}
        onSubmit={handleSubmit}
        onSaveAndDuplicate={handleSaveAndDuplicate}
        onInputChange={handleInputChange}
        onEdit={handleEditItem}
        onDelete={() => setShowDeleteConfirm(true)}
        onConfirmDelete={handleDeleteItem}
        onCancelDelete={() => setShowDeleteConfirm(false)}
      />

      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScan}
        title="Scan Item Barcode"
      />
    </div>
  );
};

export default Items;
